import React, { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { Link, useNavigate } from 'react-router-dom'
import { accountUserSettingsApi, useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import {
  type UpdateComplianceNoticeConfigBody,
  useResetComplianceFileMutation,
  useUpdateComplianceConfigMutation,
  useUpdateComplianceFileMutation,
} from '../../../../store/account/account'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import classnames from 'classnames'

import { ComplianceFileDisplayModal } from './ComplianceFileDisplayModal'
import { Nullable } from '../../../../common/Nullable'
import imageIcon from '../../../../assets/images/image-icon.svg?url'
import soundWaveIcon from '../../../../assets/images/soundwave-icon.svg?url'

import { type ComplianceFileType, type ComplianceType } from '../../../../models/Account'
import { ComplianceNoticeFileForm } from './ComplianceNoticeFile'
import { _20_MB_IN_BYTES, _5_MB_IN_BYTES } from './complianceNoticeConstants'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { PhoneComplianceSection } from './PhoneComplianceSection'
import { type ComplianceNoticeFormValues } from './ComplianceNoticeFormValues'

const DEFAULT_FORM_VALUES: ComplianceNoticeFormValues = {
  complianceNoticeEnabled: false,
  audioEnabled: false,
  inPersonAudioEnabled: false,
  imageEnabled: false,
  chatEnabled: false,
  manualAudioEnabled: false,
  periodicBeepEnabled: false,
}

export const ComplianceNoticeSettingsForm: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { hasQuota } = useZocksQuota()
  const customComplianceNoticeEnabled = hasQuota('identity.customComplianceEnabled.value')
  const phoneComplianceEnabled = hasQuota('identity.phoneComplianceEnabled.value')
  const dispatch = useAppDispatch()

  const [updateComplianceNoticeConfig, { isLoading: isConfigLoading, isSuccess: isConfigSuccess }] = useUpdateComplianceConfigMutation()
  const [updateComplianceImageFile, { isSuccess: isImageFileSuccess, isLoading: isImageFileLoading }] = useUpdateComplianceFileMutation()
  const [updateComplianceAudioFile, { isSuccess: isAudioFileSuccess, isLoading: isAudioFileLoading }] = useUpdateComplianceFileMutation()
  const [updateComplianceInPersonAudioFile, { isSuccess: isInPersonAudioFileSuccess, isLoading: isInPersonAudioFileLoading }] =
    useUpdateComplianceFileMutation()
  const [updateCompliancePhoneAudioFile, { isSuccess: isPhoneAudioFileSuccess, isLoading: isPhoneAudioFileLoading }] = useUpdateComplianceFileMutation()
  const [resetComplianceNoticeFile, { isSuccess: isFileResetSuccess }] = useResetComplianceFileMutation()
  const [showDisplayModal, setShowDisplayModal] = useState(false)
  const [complianceFile, setComplianceFile] = useState<Nullable<Blob>>(null)
  const [complianceFileType, setComplianceFileType] = useState<Nullable<ComplianceFileType>>(null)

  const isImageOverrideEnabled = !!account?.data.enterpriseConfig.complianceConfig.complianceNotice?.imageOverrideEnabled
  const isComplianceNoticeEnforced = account?.data.enterpriseConfig.complianceConfig.complianceNotice != null
  const isPhoneComplianceEnforced = account?.data.enterpriseConfig.complianceConfig.complianceNotice?.phone != null
  const showPhoneComplianceSection = phoneComplianceEnabled || isPhoneComplianceEnforced
  const effectiveComplianceNotice = account?.data.effectiveConfig.complianceConfig.complianceNotice
  const isComplianceNoticeConfigured =
    !!effectiveComplianceNotice?.audioEnabled ||
    !!effectiveComplianceNotice?.inPersonAudioEnabled ||
    !!effectiveComplianceNotice?.imageEnabled ||
    !!effectiveComplianceNotice?.chatEnabled ||
    (showPhoneComplianceSection && (!!effectiveComplianceNotice?.phone?.manualAudioEnabled || !!effectiveComplianceNotice?.phone?.periodicBeepEnabled))
  const isAudioPlayOnceOnlyEnabled = !!account?.data.enterpriseConfig.complianceConfig.complianceNotice?.audioPlayOnceOnlyEnabled

  const { setValue, control, handleSubmit, getValues } = useForm<ComplianceNoticeFormValues>({ defaultValues: DEFAULT_FORM_VALUES })
  const [imageEnabled, audioEnabled, inPersonAudioEnabled, chatEnabled] = useWatch({
    control,
    name: ['imageEnabled', 'audioEnabled', 'inPersonAudioEnabled', 'chatEnabled'],
  })

  useEffect(() => {
    if (isImageFileSuccess || isAudioFileSuccess || isInPersonAudioFileSuccess || isPhoneAudioFileSuccess || isConfigSuccess || isFileResetSuccess) {
      dispatch(accountUserSettingsApi.util.invalidateTags(['Account']))
    }
  }, [dispatch, isConfigSuccess, isImageFileSuccess, isAudioFileSuccess, isInPersonAudioFileSuccess, isPhoneAudioFileSuccess, isFileResetSuccess])

  const onMainControlChange = async (data: ComplianceNoticeFormValues) => {
    setValue('audioEnabled', data.complianceNoticeEnabled)
    setValue('inPersonAudioEnabled', data.complianceNoticeEnabled)
    setValue('imageEnabled', data.complianceNoticeEnabled)
    setValue('chatEnabled', data.complianceNoticeEnabled)
    if (phoneComplianceEnabled) {
      setValue('manualAudioEnabled', data.complianceNoticeEnabled)
      setValue('periodicBeepEnabled', data.complianceNoticeEnabled)
    }
    await onSubmit(getValues())
  }

  const onSubmit = async (data: ComplianceNoticeFormValues) => {
    if (!account) {
      return
    }
    const complianceNotice: UpdateComplianceNoticeConfigBody = {
      audioEnabled: data.audioEnabled,
      inPersonAudioEnabled: data.inPersonAudioEnabled,
      imageEnabled: data.imageEnabled,
      chatEnabled: data.chatEnabled,
      chatMessage: data.chatMessage,
    }
    if (phoneComplianceEnabled) {
      complianceNotice.phone = {
        manualAudioEnabled: data.manualAudioEnabled,
        periodicBeepEnabled: data.periodicBeepEnabled,
      }
    }

    updateComplianceNoticeConfig({
      accountId,
      body: complianceNotice,
    })
  }

  const complianceNoticeEnabled = useMemo(() => {
    return !!account && customComplianceNoticeEnabled
  }, [account, customComplianceNoticeEnabled])

  useEffect(() => {
    if (!account) {
      return
    }

    setValue('complianceNoticeEnabled', isComplianceNoticeConfigured)
    setValue('manualAudioEnabled', !!effectiveComplianceNotice?.phone?.manualAudioEnabled)
    setValue('periodicBeepEnabled', !!effectiveComplianceNotice?.phone?.periodicBeepEnabled)
    setValue('audioEnabled', !!effectiveComplianceNotice?.audioEnabled)
    setValue('inPersonAudioEnabled', !!effectiveComplianceNotice?.inPersonAudioEnabled)
    setValue('imageEnabled', !!effectiveComplianceNotice?.imageEnabled)
    setValue('chatEnabled', !!effectiveComplianceNotice?.chatEnabled)
    setValue('chatMessage', effectiveComplianceNotice?.chatMessage ?? '')
  }, [account, effectiveComplianceNotice, isComplianceNoticeConfigured, setValue])

  const displayFile = (fileContent: Nullable<Blob>, fileType: ComplianceFileType) => {
    setShowDisplayModal(true)
    setComplianceFile(fileContent)
    setComplianceFileType(fileType)
  }

  const hideModal = () => {
    setShowDisplayModal(false)
    setComplianceFile(null)
    setComplianceFileType(null)
  }

  const updateComplianceFile = (fileId: string, fileType: ComplianceFileType, type: ComplianceType) => {
    const audioMutationsByType = {
      default: updateComplianceAudioFile,
      'in-person': updateComplianceInPersonAudioFile,
      phone: updateCompliancePhoneAudioFile,
    }
    const mutation = fileType === 'image' ? updateComplianceImageFile : audioMutationsByType[type]
    mutation({
      accountId,
      body: {
        fileId,
        fileType,
        type,
      },
    })
  }

  const resetComplianceFile = (fileType: ComplianceFileType, type: ComplianceType) => {
    resetComplianceNoticeFile({
      accountId,
      body: {
        fileType,
        type,
      },
    })
  }

  return (
    <>
      <ComplianceFileDisplayModal
        complianceFile={complianceFile}
        show={showDisplayModal}
        onHide={hideModal}
        type={complianceFileType}
      ></ComplianceFileDisplayModal>
      <Container fluid>
        <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <Stack direction='horizontal' gap={3}>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  You can notify your clients about an AI Assistant taking part in your meeting and you can use this to collect implied consent. The compliance
                  notice consists of a voice message and a picture for the AI Assistant joining.
                </span>
              </div>
              {!complianceNoticeEnabled && !isComplianceNoticeEnforced && !isComplianceNoticeConfigured && (
                <ScopedBlock
                  scope={{
                    scope: 'compliance_notice:update',
                    superScope: ['compliance_notice:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    className='d-flex p-1 p-2 align-items-center text-nowrap'
                    variant='primary'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    <img alt='Upgrade' src={PrizeBadge} />
                    Get feature
                  </Button>
                </ScopedBlock>
              )}
            </Stack>
          </Col>
        </Row>
        {isComplianceNoticeEnforced ? (
          <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Compliance notice is managed at the enterprise level.</span>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
          <Col className='p-0 d-flex gap-4'>
            <Controller
              name='complianceNoticeEnabled'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'compliance_notice:update',
                      superScope: ['compliance_notice:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onMainControlChange)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
            <div className='paragraph-medium'>I want to set a compliance notice for all users in this account.</div>
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <Row className='zocks-mb-2'>
          <Col className='p-0'>
            <h4 className='zocks-text-gray-900'>Compliance notice components</h4>
          </Col>
        </Row>

        <Row
          className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
            'zocks-border-gray-200': !imageEnabled,
            'zocks-border-primary': imageEnabled,
          })}
        >
          <Col>
            <div className='d-flex flex-row zocks-gap-1.25 zocks-mb-2'>
              <Controller
                name='imageEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'compliance_notice:update',
                        superScope: ['compliance_notice:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Check
                        disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmit)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
              <div>
                <span className='text-uppercase lead-medium'>Assistant image</span>
                <div>
                  You can use this to display your own company logo. Recommended image parameters (640x480 .jpg or .png, max size 20MB). You can find a more
                  detailed description on the{' '}
                  <Link className='zocks-text-gray-900 text-decoration-underline' to='https://help.zocks.io/en/collections/9209396-security-and-compliance'>
                    Help desk
                  </Link>{' '}
                  menu.
                </div>
              </div>
            </div>

            <ComplianceNoticeFileForm
              uploadEnabled={complianceNoticeEnabled && imageEnabled && !isAccountFetching && (!isComplianceNoticeEnforced || isImageOverrideEnabled)}
              restoreEnabled={complianceNoticeEnabled && imageEnabled && !isAccountFetching && (!isComplianceNoticeEnforced || isImageOverrideEnabled)}
              fileType='image'
              complianceFileInfo={account?.data?.effectiveConfig.complianceConfig.complianceNotice?.imageFileOriginal || null}
              buttonBackgroundImage={imageIcon}
              maxFileSize={_20_MB_IN_BYTES}
              downloadFileCallback={displayFile}
              uploadFileCallback={updateComplianceFile}
              resetFileCallback={resetComplianceFile}
              supportedMimeTypes='image/png,image/jpeg'
              isLoading={isImageFileLoading}
              type='default'
            />
          </Col>
        </Row>

        <Row
          className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
            'zocks-border-gray-200': !audioEnabled,
            'zocks-border-primary': audioEnabled,
          })}
        >
          <Col>
            <div className='d-flex flex-row zocks-gap-1.25 zocks-mb-2'>
              <Controller
                name='audioEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'compliance_notice:update',
                        superScope: ['compliance_notice:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Check
                        disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmit)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
              <div>
                <span className='text-uppercase lead-medium'>Voice</span>
                <div>You can add audio file up to 5MB to customize the default message.</div>
                {audioEnabled && isAudioPlayOnceOnlyEnabled && (
                  <Stack direction='horizontal' gap={3} className='zocks-mt-1.25 p-3 zocks-bg-color-primary-light justify-content-center rounded'>
                    <div className='d-flex align-self-center gap-3 align-items-center'>
                      <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                      <span className='zocks-text-gray-900'>The voice compliance notice plays once per meeting. This is managed at the enterprise level.</span>
                    </div>
                  </Stack>
                )}
              </div>
            </div>
            <ComplianceNoticeFileForm
              uploadEnabled={(audioEnabled || complianceNoticeEnabled || isAccountFetching) && !isComplianceNoticeEnforced}
              restoreEnabled={(audioEnabled || complianceNoticeEnabled || isAccountFetching) && !isComplianceNoticeEnforced}
              fileType='audio'
              complianceFileInfo={account?.data?.effectiveConfig.complianceConfig.complianceNotice?.audioFileOriginal || null}
              buttonBackgroundImage={soundWaveIcon}
              maxFileSize={_5_MB_IN_BYTES}
              downloadFileCallback={displayFile}
              uploadFileCallback={updateComplianceFile}
              resetFileCallback={resetComplianceFile}
              supportedMimeTypes='audio/wav,audio/mp3'
              isLoading={isAudioFileLoading}
              type='default'
            />
          </Col>
        </Row>

        <Row
          className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
            'zocks-border-gray-200': !inPersonAudioEnabled,
            'zocks-border-primary': inPersonAudioEnabled,
          })}
        >
          <Col>
            <div className='d-flex flex-row zocks-gap-1.25 zocks-mb-2'>
              <Controller
                name='inPersonAudioEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'compliance_notice:update',
                        superScope: ['compliance_notice:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Check
                        disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmit)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
              <div>
                <span className='text-uppercase lead-medium'>In Person disclosure</span>
                <div>You can add audio file up to 5 MB to play automatically when an in-person meeting is started from the browser or mobile app.</div>
              </div>
            </div>
            <ComplianceNoticeFileForm
              uploadEnabled={(inPersonAudioEnabled || complianceNoticeEnabled || isAccountFetching) && !isComplianceNoticeEnforced}
              restoreEnabled={(inPersonAudioEnabled || complianceNoticeEnabled || isAccountFetching) && !isComplianceNoticeEnforced}
              fileType='audio'
              complianceFileInfo={account?.data?.effectiveConfig.complianceConfig.complianceNotice?.inPersonAudioFileOriginal || null}
              buttonBackgroundImage={soundWaveIcon}
              maxFileSize={_5_MB_IN_BYTES}
              downloadFileCallback={displayFile}
              uploadFileCallback={updateComplianceFile}
              resetFileCallback={resetComplianceFile}
              supportedMimeTypes='audio/wav,audio/mp3'
              isLoading={isInPersonAudioFileLoading}
              type='in-person'
            />
          </Col>
        </Row>

        <Row
          className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
            'zocks-border-gray-200': !chatEnabled,
            'zocks-border-primary': chatEnabled,
          })}
        >
          <Col>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='d-flex flex-row zocks-gap-1.25 zocks-mb-2'>
                <Controller
                  name='chatEnabled'
                  control={control}
                  render={({ field }) => {
                    return (
                      <ScopedBlock
                        scope={{
                          scope: 'compliance_notice:update',
                          superScope: ['compliance_notice:admin'],
                        }}
                        missingScopeAction={MissingScopeAction.DISABLE}
                      >
                        <Form.Check
                          disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e)
                            handleSubmit(onSubmit)()
                          }}
                        />
                      </ScopedBlock>
                    )
                  }}
                />
                <div>
                  <span className='text-uppercase lead-medium'>Chat message</span>
                  <div>You can use this to automatically post a compliance message in Zoom or Microsoft Teams chat when participants join.</div>
                  <Stack direction='horizontal' gap={3} className='zocks-mt-1.25 p-3 zocks-bg-color-primary-light justify-content-center rounded'>
                    <div className='d-flex align-self-center gap-3 align-items-center'>
                      <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                      <span className='zocks-text-gray-900'>
                        <b>Zoom</b>: the message is posted in the meeting chat to every participant as a private message. Participants joining by phone won't
                        receive it. <br />
                        <b>Microsoft Teams</b>: the message is posted in the meeting chat. Participants joining by phone won't receive it.
                      </span>
                    </div>
                  </Stack>
                </div>
              </div>
              <Controller
                name='chatMessage'
                control={control}
                render={({ field, fieldState }) => (
                  <Row>
                    <Col>
                      <ScopedBlock
                        scope={{
                          scope: 'compliance_notice:update',
                          superScope: ['compliance_notice:admin'],
                        }}
                        missingScopeAction={MissingScopeAction.DISABLE}
                      >
                        <Form.Control
                          id={field.name}
                          as='textarea'
                          rows={3}
                          placeholder='Customize'
                          {...field}
                          disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                        />
                        {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                      </ScopedBlock>
                    </Col>
                  </Row>
                )}
              />
              <ScopedBlock
                scope={{
                  scope: 'compliance_notice:update',
                  superScope: ['compliance_notice:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button
                  variant='primary'
                  type='submit'
                  disabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
                  className='mt-3'
                >
                  Save Changes
                </Button>
              </ScopedBlock>
            </form>
          </Col>
        </Row>

        {showPhoneComplianceSection ? (
          <PhoneComplianceSection
            control={control}
            togglesDisabled={!complianceNoticeEnabled || isComplianceNoticeEnforced || isConfigLoading || isAccountFetching}
            fileActionsEnabled={complianceNoticeEnabled && !isAccountFetching && !isComplianceNoticeEnforced}
            phoneNotice={account?.data?.effectiveConfig.complianceConfig.complianceNotice?.phone}
            onToggleChange={() => {
              handleSubmit(onSubmit)()
            }}
            displayFile={displayFile}
            updateComplianceFile={updateComplianceFile}
            resetComplianceFile={resetComplianceFile}
            isFileLoading={isPhoneAudioFileLoading}
          />
        ) : null}
      </Container>
    </>
  )
}
